import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  RadioGroup,
  RadioGroupItem,
} from './ui/radio-group';
import { useToast } from '../hooks/use-toast';
import { trackEvent } from '../lib/analytics';
import { CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SessionRequestForm = ({ compact = false }) => {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    firstName: '',
    location: '',
    email: '',
    message: '',
    adult: '',
  });

  const handleChange = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.adult !== 'yes') {
      toast({
        title: 'Age verification required',
        description: 'You must confirm you are 18 or older to submit a request.',
      });
      return;
    }
    if (!form.email || !form.message) {
      toast({
        title: 'Missing information',
        description: 'Please provide your email and message.',
      });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/session-request`, {
        first_name: form.firstName || null,
        location: form.location || null,
        email: form.email.trim(),
        message: form.message.trim(),
        adult: form.adult,
      });
      setSubmitted(true);
      trackEvent('session_request');
      toast({ title: 'Request received', description: 'Thank you. I will contact you privately.' });
    } catch (err) {
      toast({
        title: 'Submission failed',
        description: err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || 'Please try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-white border border-neutral-200 p-10 md:p-14 text-center">
        <CheckCircle2 size={48} className="mx-auto text-neutral-900 mb-4" />
        <h3 className="font-serif text-3xl text-neutral-900 mb-3">Request Received</h3>
        <p className="text-neutral-600 max-w-md mx-auto">
          Thank you for reaching out. Your message is confidential. You will receive a private reply
          within 24-48 hours.
        </p>
        <Button
          onClick={() => {
            setSubmitted(false);
            setForm({ firstName: '', location: '', email: '', message: '', adult: '' });
          }}
          className="mt-8 rounded-none bg-neutral-900 text-white hover:bg-neutral-700 h-11 px-8 text-sm tracking-widest uppercase"
        >
          Send Another
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white border border-neutral-200 p-8 md:p-12 space-y-6">
      {!compact && (
        <div className="mb-6">
          <p className="text-neutral-500 text-xs uppercase tracking-[0.3em] mb-3">Get in touch</p>
          <h3 className="font-serif text-3xl md:text-4xl text-neutral-900 font-bold">
            Spanking Request
          </h3>
          <p className="text-neutral-600 mt-3 text-sm">
            All communication is confidential. First sessions are always free.
          </p>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <Label htmlFor="firstName" className="text-xs uppercase tracking-widest text-neutral-700">
            First Name
          </Label>
          <Input
            id="firstName"
            value={form.firstName}
            onChange={handleChange('firstName')}
            className="mt-2 rounded-none border-neutral-300 focus-visible:ring-neutral-900 h-11"
          />
        </div>
        <div>
          <Label htmlFor="location" className="text-xs uppercase tracking-widest text-neutral-700">
            Location
          </Label>
          <Input
            id="location"
            value={form.location}
            onChange={handleChange('location')}
            className="mt-2 rounded-none border-neutral-300 focus-visible:ring-neutral-900 h-11"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="email" className="text-xs uppercase tracking-widest text-neutral-700">
          Your Email *
        </Label>
        <Input
          id="email"
          type="email"
          required
          value={form.email}
          onChange={handleChange('email')}
          className="mt-2 rounded-none border-neutral-300 focus-visible:ring-neutral-900 h-11"
        />
      </div>

      <div>
        <Label htmlFor="message" className="text-xs uppercase tracking-widest text-neutral-700">
          Message *
        </Label>
        <Textarea
          id="message"
          required
          rows={6}
          value={form.message}
          onChange={handleChange('message')}
          className="mt-2 rounded-none border-neutral-300 focus-visible:ring-neutral-900 resize-none"
          placeholder="Please describe what you're looking for..."
        />
      </div>

      <div>
        <Label className="text-xs uppercase tracking-widest text-neutral-700">
          Are you +18 years old?
        </Label>
        <RadioGroup
          value={form.adult}
          onValueChange={(v) => setForm({ ...form, adult: v })}
          className="flex gap-6 mt-3"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="yes" id="adult-yes" />
            <Label htmlFor="adult-yes" className="cursor-pointer">Yes</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="no" id="adult-no" />
            <Label htmlFor="adult-no" className="cursor-pointer">No</Label>
          </div>
        </RadioGroup>
      </div>

      <Button
        type="submit"
        disabled={submitting}
        className="w-full rounded-none bg-neutral-900 text-white hover:bg-neutral-700 h-12 text-sm tracking-widest uppercase disabled:opacity-60"
      >
        {submitting ? 'Sending...' : 'Submit Request'}
      </Button>
      <p className="text-xs text-neutral-500 text-center">Your information stays private and is never shared.</p>
    </form>
  );
};

export default SessionRequestForm;
